import { useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { manualTransactionContract } from "@common/contracts/manual-transaction.contract";
import {
    ApproveAccrualReviewBody,
    LineItemAllocation,
} from "@common/schemas/domains/accrualReview/accrual-review.schema";
import { ApproveWithScheduleResolutionsOutcome } from "@components/accounting/schedules/linkedConflict/useApproveWithScheduleResolutions";
import {
    INVOICE_ACCRUAL_MATCHES_QUERY_KEY,
    invalidateInvoiceQueries,
} from "@api/invoice.api";
import {
    finalizeTransactionDeletion,
    markTransactionForDeletion,
} from "@api/transactionDeletion.api";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { getBackendAPIClient } from "@lib/backendAPIClient";

const manualTransactionContractClient = getBackendAPIClient(
    manualTransactionContract,
);

export interface RecordInvoicePaymentParams {
    workspaceId: string;
    invoiceId: string;
    entityId: number;
    amount: number;
    paymentDate: string;
    description: string;
    categoryId: string;
    counterpartyId?: string | null;
    lineItemAllocations?: LineItemAllocation[] | null;
}

export type RecordInvoicePaymentApprove = (options: {
    workspaceId: string;
    body: ApproveAccrualReviewBody;
}) => Promise<ApproveWithScheduleResolutionsOutcome>;

type PaymentTransactionInput = Pick<
    RecordInvoicePaymentParams,
    | "entityId"
    | "amount"
    | "paymentDate"
    | "description"
    | "categoryId"
    | "counterpartyId"
>;

interface PendingPaymentTransaction {
    id: number;
    input: PaymentTransactionInput;
    isMarkedForDeletion: boolean;
}

function isSamePaymentTransactionInput(
    left: PaymentTransactionInput,
    right: PaymentTransactionInput,
): boolean {
    return (
        left.entityId === right.entityId &&
        left.amount === right.amount &&
        left.paymentDate === right.paymentDate &&
        left.description.trim() === right.description.trim() &&
        left.categoryId === right.categoryId &&
        (left.counterpartyId ?? null) === (right.counterpartyId ?? null)
    );
}

async function createPaymentTransaction({
    entityId,
    amount,
    paymentDate,
    description,
    categoryId,
    counterpartyId,
}: PaymentTransactionInput): Promise<number> {
    const trimmedDescription = description.trim();
    const created =
        await manualTransactionContractClient.createManualTransactions({
            body: {
                entityId,
                createCashAccount: true,
                transactions: [
                    {
                        categoryId,
                        amount,
                        dateOnly: paymentDate,
                        memo: trimmedDescription || undefined,
                        bankDescription: trimmedDescription || undefined,
                        ...(counterpartyId != null ? { counterpartyId } : {}),
                    },
                ],
            },
        });

    const transactionId =
        created.status === 200 ? created.body.transactions[0]?.id : undefined;
    if (transactionId == null) {
        throw new Error("Failed to create payment transaction");
    }
    return transactionId;
}

function assertPaymentTransactionDiscarded(response: { status: number }) {
    if (response.status !== 200) {
        throw new Error("Failed to discard previous payment transaction");
    }
}

export function useRecordInvoicePayment(options: {
    approve: RecordInvoicePaymentApprove;
}) {
    const queryClient = useQueryClient();
    const { toast } = useToaster();
    // Remembers a created but unmatched cash transaction so a retry reuses it
    // instead of duplicating it, or deletes it when the payment changed.
    const pendingTransactionRef = useRef<PendingPaymentTransaction | null>(
        null,
    );

    return useMutation({
        mutationFn: async ({
            workspaceId,
            invoiceId,
            lineItemAllocations,
            ...input
        }: RecordInvoicePaymentParams) => {
            const pending = pendingTransactionRef.current;
            if (
                pending != null &&
                (pending.isMarkedForDeletion ||
                    !isSamePaymentTransactionInput(pending.input, input))
            ) {
                if (!pending.isMarkedForDeletion) {
                    assertPaymentTransactionDiscarded(
                        await markTransactionForDeletion(pending.id),
                    );
                    pendingTransactionRef.current = {
                        ...pending,
                        isMarkedForDeletion: true,
                    };
                }
                assertPaymentTransactionDiscarded(
                    await finalizeTransactionDeletion(pending.id),
                );
                pendingTransactionRef.current = null;
            }

            let transactionId = pendingTransactionRef.current?.id;
            if (transactionId == null) {
                transactionId = await createPaymentTransaction(input);
                pendingTransactionRef.current = {
                    id: transactionId,
                    input,
                    isMarkedForDeletion: false,
                };
            }

            const outcome = await options.approve({
                workspaceId,
                body: {
                    transactionId,
                    ...(lineItemAllocations
                        ? { lineItemAllocations }
                        : { invoiceIds: [invoiceId] }),
                },
            });
            if (outcome !== "approved") {
                return outcome;
            }

            pendingTransactionRef.current = null;
            return outcome;
        },
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: ["transactions"],
            });
        },
        onSuccess: (outcome) => {
            if (outcome !== "approved") {
                return;
            }
            invalidateInvoiceQueries();
            queryClient.invalidateQueries({
                queryKey: [INVOICE_ACCRUAL_MATCHES_QUERY_KEY],
            });
            toast("Invoice marked as paid");
        },
    });
}
